import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Loader2, Pencil, PiggyBank, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import budgetService from '@services/budgetService';
import { BUDGET_SOURCE_LABELS, BUDGET_STATUS_LABELS, type BudgetItem, type BudgetReport } from '@/types/finance';
import { formatBRL } from '@/utils/money';
import { currentMonth, type YearMonth } from '@/utils/period';
import { budgetStatusColor, budgetStatusOf, clampPct, formatPct } from '@/utils/reportUtils';
import { ColorChip } from './financeBits';
import { MonthStepper, StatCard } from './reportBits';
import { BudgetFormModal } from './BudgetFormModal';
import { buildBudgetTree } from './reportHelpers';
import { ReportEntriesDrawer, type ReportEntriesTarget } from './ReportEntriesDrawer';

/** Barra de progresso colorida pela situação (ok verde · atenção âmbar · estourado vermelho), com o %. */
const BudgetBar: React.FC<{ item: Pick<BudgetItem, 'pct' | 'status' | 'budget_cents' | 'spent_cents'>; className?: string }> = ({ item, className }) => {
  const tone = budgetStatusColor(item.status);
  if (!item.budget_cents) return <span className={clsx('text-xs text-slate-400', className)}>sem orçamento</span>;
  const pct = item.pct ?? (item.budget_cents ? (item.spent_cents / item.budget_cents) * 100 : 0);
  return (
    <div className={clsx('flex items-center gap-2', className)} title={`${BUDGET_STATUS_LABELS[item.status]} · ${formatPct(pct)} do orçamento`}>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clampPct(pct))}>
        <div className={clsx('h-full rounded-full transition-all duration-200', tone.bar)} style={{ width: `${clampPct(pct)}%` }} />
      </div>
      <span className={clsx('text-xs font-medium w-12 text-right tabular-nums', tone.text)}>{formatPct(pct)}</span>
    </div>
  );
};

/** Etiqueta da origem do orçamento efetivo: "padrão" / "só este mês". */
const SourceBadge: React.FC<{ item: BudgetItem }> = ({ item }) => {
  if (item.budget_source === 'none') return null;
  return (
    <span className={clsx('badge-neutral', item.budget_source === 'competence' && '!bg-sky-100 !text-sky-700')} title={item.budget_source === 'competence' ? `Exceção deste mês${item.default_cents ? ` (padrão ${formatBRL(item.default_cents)})` : ''}` : 'Padrão mensal'}>
      {BUDGET_SOURCE_LABELS[item.budget_source]}
    </span>
  );
};

/**
 * Finanças → Orçamento: GET /reports/budget?year&month — cartões (orçado, gasto realizado,
 * previsto, % geral), tabela em árvore das categorias de despesa com orçamento efetivo (padrão ou
 * exceção do mês), gasto, previsto e barra por situação; "Definir orçamento" por linha
 * (BudgetFormModal → PUT/DELETE /categories/:id/budget); clicar no gasto abre o drill-down.
 */
export const BudgetView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const [searchParams, setSearchParams] = useSearchParams();
  const [month, setMonth] = useState<YearMonth>(() => {
    const y = Number(searchParams.get('year'));
    const m = Number(searchParams.get('month'));
    return y > 1900 && m >= 1 && m <= 12 ? { year: y, month: m } : currentMonth();
  });
  const [report, setReport] = useState<BudgetReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [onlyBudgeted, setOnlyBudgeted] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<BudgetItem | null>(null);
  const [drill, setDrill] = useState<ReportEntriesTarget | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await budgetService.getReport(month.year, month.month));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const changeMonth = (ym: YearMonth) => {
    setMonth(ym);
    const next = new URLSearchParams(searchParams);
    next.set('year', String(ym.year));
    next.set('month', String(ym.month));
    setSearchParams(next, { replace: true });
  };

  const tree = useMemo(() => {
    const all = buildBudgetTree(report?.items ?? []);
    if (!onlyBudgeted) return all;
    return all
      .map((n) => ({ item: n.item, children: n.children.filter((c) => !!c.budget_cents) }))
      .filter((n) => !!n.item.budget_cents || n.children.length > 0);
  }, [report, onlyBudgeted]);

  const overall = useMemo(() => {
    if (!report || !report.total_budget_cents) return null;
    const pct = (report.total_spent_cents / report.total_budget_cents) * 100;
    return { pct, status: budgetStatusOf(report.total_spent_cents, report.total_budget_cents) };
  }, [report]);

  const budgetedCount = useMemo(() => (report?.items ?? []).filter((i) => !!i.budget_cents).length, [report]);

  const openDrill = (item: BudgetItem, isRoot: boolean) =>
    setDrill({ year: month.year, month: month.month, categoryId: item.category_id, includeChildren: isRoot, basis: 'projected', title: item.name, color: item.color });

  const spentCell = (item: BudgetItem, isRoot: boolean) => (
    <button type="button" className="tabular-nums text-slate-800 hover:text-emerald-700 hover:underline" title="Ver os lançamentos" onClick={() => openDrill(item, isRoot)}>
      {formatBRL(item.spent_cents)}
    </button>
  );

  const actionCell = (item: BudgetItem) =>
    canWrite ? (
      item.budget_cents ? (
        <ActionButton icon={<Pencil size={15} />} variant="edit" tooltip="Alterar orçamento" onClick={() => setEditing(item)} />
      ) : (
        <button type="button" className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline whitespace-nowrap px-2 py-1" onClick={() => setEditing(item)}>
          <Plus size={13} /> Definir
        </button>
      )
    ) : null;

  const rowCls = (item: BudgetItem, depth: number) => clsx('table-row', depth > 0 && 'bg-slate-50/60', item.status === 'exceeded' && 'bg-red-50/40');

  const row = (item: BudgetItem, depth: number, isRoot: boolean) => (
    <tr key={item.category_id} className={rowCls(item, depth)}>
      <td className={clsx('table-cell', depth > 0 && 'pl-9')}>
        <ColorChip color={item.color}>{item.name}</ColorChip>
      </td>
      <td className="table-cell whitespace-nowrap">
        {item.budget_cents ? (
          <span className="inline-flex items-center gap-1.5 tabular-nums">
            {formatBRL(item.budget_cents)} <SourceBadge item={item} />
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="table-cell text-right whitespace-nowrap">{spentCell(item, isRoot)}</td>
      <td className="table-cell text-right whitespace-nowrap">
        <span className={clsx('tabular-nums', item.projected_cents !== item.spent_cents ? 'italic text-slate-600' : 'text-slate-400')}>{formatBRL(item.projected_cents)}</span>
      </td>
      <td className="table-cell min-w-[11rem]">
        <BudgetBar item={item} />
      </td>
      <td className="table-cell text-right">{actionCell(item)}</td>
    </tr>
  );

  const mobileRow = (item: BudgetItem, depth: number, isRoot: boolean) => (
    <div key={item.category_id} className={clsx('px-3 py-2.5 space-y-1.5', depth > 0 && 'pl-7 bg-slate-50/60', item.status === 'exceeded' && 'bg-red-50/40')}>
      <div className="flex items-center justify-between gap-2">
        <ColorChip color={item.color} className="text-sm text-slate-800 font-medium">
          {item.name}
        </ColorChip>
        {actionCell(item)}
      </div>
      <BudgetBar item={item} />
      <p className="text-small flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span>
          gasto {spentCell(item, isRoot)}
          {item.projected_cents !== item.spent_cents && <span className="italic"> · previsto {formatBRL(item.projected_cents)}</span>}
        </span>
        {item.budget_cents ? (
          <span className="inline-flex items-center gap-1">
            de {formatBRL(item.budget_cents)} <SourceBadge item={item} />
          </span>
        ) : null}
      </p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <PiggyBank size={20} className="text-slate-500" /> Orçamento
        </h1>
        <div className="grid grid-cols-1 sm:flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="checkbox-field" checked={onlyBudgeted} onChange={(e) => setOnlyBudgeted(e.target.checked)} />
            Só com orçamento
          </label>
          <Button variant="outline" size="sm" icon={<RefreshCw />} onClick={load} disabled={loading}>
            Atualizar
          </Button>
        </div>
      </div>

      <div className="card p-3 sm:p-4">
        <MonthStepper value={month} onChange={changeMonth} disabled={loading} />
      </div>

      {report && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Orçado" cents={report.total_budget_cents}>
            {budgetedCount === 0 ? 'nenhuma categoria orçada' : `${budgetedCount} ${budgetedCount === 1 ? 'categoria orçada' : 'categorias orçadas'}`}
          </StatCard>
          <StatCard label="Gasto realizado" cents={report.total_spent_cents} tone="expense">
            nas categorias orçadas · até hoje
          </StatCard>
          <StatCard label="Previsto no mês" cents={report.total_projected_cents}>
            {report.total_projected_cents > report.total_spent_cents ? <span className="italic">inclui {formatBRL(report.total_projected_cents - report.total_spent_cents)} a vencer</span> : 'sem lançamentos futuros'}
          </StatCard>
          <div className="card p-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Uso geral</p>
            {overall ? (
              <>
                <p className={clsx('text-lg font-semibold tabular-nums', budgetStatusColor(overall.status).text)}>{formatPct(overall.pct)}</p>
                <BudgetBar item={{ pct: overall.pct, status: overall.status, budget_cents: report.total_budget_cents, spent_cents: report.total_spent_cents }} className="mt-1" />
              </>
            ) : (
              <p className="text-sm text-slate-400 mt-1">Defina um orçamento nas categorias abaixo.</p>
            )}
          </div>
        </div>
      )}

      {loading && !report ? (
        <p className="text-center text-slate-400 py-10">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      ) : tree.length === 0 ? (
        <p className="text-center text-slate-500 py-8">
          {onlyBudgeted ? 'Nenhuma categoria com orçamento neste mês.' : 'Nenhuma categoria de despesa ativa.'}{' '}
          {!onlyBudgeted && (
            <Link to="/finance/categories" className="text-emerald-700 hover:underline">
              Cadastre em Categorias
            </Link>
          )}
        </p>
      ) : (
        <>
          <div className={clsx('md:hidden card divide-y divide-slate-100 overflow-hidden', loading && 'opacity-60')}>
            {tree.map((n) => (
              <React.Fragment key={n.item.category_id}>
                {mobileRow(n.item, 0, true)}
                {n.children.map((c) => mobileRow(c, 1, false))}
              </React.Fragment>
            ))}
          </div>
          <div className={clsx('table-container bg-white hidden md:block', loading && 'opacity-60')}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th>Categoria</th>
                    <th>Orçamento</th>
                    <th className="text-right">Gasto</th>
                    <th className="text-right">Previsto</th>
                    <th>Uso</th>
                    <th className="text-right" />
                  </tr>
                </thead>
                <tbody>
                  {tree.map((n) => (
                    <React.Fragment key={n.item.category_id}>
                      {row(n.item, 0, true)}
                      {n.children.map((c) => row(c, 1, false))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <p className="text-small">
        O gasto soma lançamentos de conta e itens de cartão da categoria no mês (sem transferências nem pagamento de fatura). <strong>Gasto</strong> é o realizado até hoje;{' '}
        <strong>Previsto</strong> inclui os lançamentos futuros. Categoria principal compara com ela mais as subcategorias. Clique no gasto para ver os lançamentos.
      </p>

      {editing && (
        <BudgetFormModal
          item={editing}
          month={month}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          onError={setError}
        />
      )}

      <ReportEntriesDrawer target={drill} onClose={() => setDrill(null)} onError={setError} />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default BudgetView;
